import { useCallback, useMemo } from 'react';

import { getPromotionEndDate, getUnitType } from '../OrderSummaryPage.utils';

import { useMileageTiers } from '~/api/easy-estimator-api/mileage-api/useMileageTiers';
import { useOptionalItems } from '~/api/easy-estimator-api/optional-items-api/useOptionalItems';
import {
  BACKSPLASH_ID,
  LOCAL_STORAGE_KEY,
  WATERFALL_ID,
} from '~/core/constants';
import { getSearchParameter } from '~/core/helpers';
import { useRetailerSettings } from '~/core/hooks/useRetailerSettings';
import { useSmallJobs } from '~/core/hooks/useSmallJobs';
import { OptionalProduct, getEffectiveDiscountOverride } from '~/core/models';
import { formatPriceUnitOrFree } from '~/core/utils';
import { usePricedProductsContext } from '~/legacy/easy-estimator-page/context';
import { useAppSelector } from '~/redux/hooks';
import { selectSelectedColor } from '~/redux/slices/color';
import { selectDiscountOverride } from '~/redux/slices/estimate';
import {
  CustomLaborData,
  selectPickedCustomLaborOptionalItems,
} from '~/redux/slices/optionalItems';

// Constants for pricing overrides - centralized for future implementation
const PRICING_OVERRIDES = {
  INSTALLATION_COST: 0, // for possible implementation of non included costs
  DELIVERY_COST: 0, // for future implementation on "mileage"
} as const;

type OrderInvoiceItem = {
  label: string;
  notes?: string[];
  measure: string;
  price: string;
  showLineItemQuantityWarning?: boolean;
};

type UseOrderInvoiceReturn = {
  subtotal: number;
  savings: number;
  discountOverride: number;
  maximumDiscount: number;
  displayTotal: string;
  items: OrderInvoiceItem[];
  promotionEndDate?: string;
  deliveryCost?: number;
};

/**
 * Custom hook for managing order invoice data and calculations
 *
 * Processes priced products, calculates totals including savings, and formats
 * invoice items for display. Handles small job scenarios with quantity warnings
 * and pricing adjustments.
 *
 * @returns {UseOrderInvoiceReturn} Object containing invoice items, totals, and metadata
 */
export const useOrderInvoice = (): UseOrderInvoiceReturn => {
  const {
    pricedProductsWithoutDelivery,
    priceTotalsWithoutDelivery: {
      priceWithoutDiscount: subtotal,
      priceWithDiscount,
    },
    priceTotalsWithoutDelivery: { priceWithDiscount: productsAfterSavings },
  } = usePricedProductsContext();
  const { matchingMileageTier } = useMileageTiers();

  const deliveryCost = matchingMileageTier?.price ?? 0;

  const selectedColor = useAppSelector(selectSelectedColor);
  const discountOverride = useAppSelector(selectDiscountOverride);
  const { retailerSettings } = useRetailerSettings();
  const userToken = getSearchParameter(LOCAL_STORAGE_KEY.USER_TOKEN);
  const maximumDiscount = Math.max(productsAfterSavings, 0);
  const effectiveDiscountOverride = getEffectiveDiscountOverride(
    maximumDiscount,
    discountOverride,
    retailerSettings?.userOptions,
    userToken
  );
  const selectedOptionalProducts = useAppSelector(
    selectPickedCustomLaborOptionalItems
  );
  const { useMinQtyAreaForPricing } = useSmallJobs();

  const { optionalItems } = useOptionalItems();

  /**
   * Helper function to generate formatted notes for a specific product
   * Extracted for better separation of concerns and testability
   *
   * @param productId - The ID of the product to get notes for
   * @param selectedItems - Array of selected items for the product
   * @param optionalItemsList - Array of available optional items
   * @returns Array of formatted note strings with pricing information
   */
  const getItemNotesForProduct = useCallback(
    (
      productId: number,
      selectedItems: CustomLaborData[],
      optionalItemsList: OptionalProduct[]
    ) => {
      const item = optionalItemsList.find(
        (item) => item.productId === productId
      );

      if (!item?.price) {
        return [];
      }

      return selectedItems
        .filter(({ quantity }) => quantity > 0) // More explicit check
        .map(
          ({ quantity, notes }) =>
            `($${(item.price * quantity).toFixed(2)}) ${notes}`
        );
    },
    []
  );

  /**
   * Generates formatted notes for optional product items with pricing details
   *
   * @param productId - The ID of the product to get notes for
   * @returns Array of formatted note strings with pricing information
   *
   * @example
   * // Returns: ["($150.00) Custom edge work", "($75.00) Additional cutout"]
   * const notes = getItemNotes(123);
   */
  const getItemNotes = useCallback(
    (productId: number): string[] => {
      const selectedItems = selectedOptionalProducts?.[productId] || [];

      return getItemNotesForProduct(productId, selectedItems, optionalItems);
    },
    [optionalItems, selectedOptionalProducts, getItemNotesForProduct]
  );

  const items: OrderInvoiceItem[] = useMemo(
    () =>
      pricedProductsWithoutDelivery.map((product) => ({
        label: product.consumerDesc,
        measure: `${product.quantity.toFixed(0)} ${getUnitType(product)}`,
        price: formatPriceUnitOrFree(
          product.priceWithoutDiscount || product.price
        ),
        notes: getItemNotes(product.productId),
        showLineItemQuantityWarning:
          useMinQtyAreaForPricing &&
          (product.name === selectedColor?.name ||
            product.lineItemId === BACKSPLASH_ID ||
            product.lineItemId === WATERFALL_ID),
      })),
    [
      getItemNotes,
      pricedProductsWithoutDelivery,
      selectedColor?.name,
      useMinQtyAreaForPricing,
    ]
  );

  const savings = subtotal - priceWithDiscount;

  const finalCalculatedTotal = Math.max(
    subtotal -
      savings -
      (effectiveDiscountOverride ?? 0) +
      PRICING_OVERRIDES.INSTALLATION_COST +
      deliveryCost,
    0
  );

  const formattedTotalPrice = formatPriceUnitOrFree(finalCalculatedTotal);

  const displayTotal = useMinQtyAreaForPricing
    ? `${formattedTotalPrice}*`
    : formattedTotalPrice;

  return {
    subtotal,
    savings,
    discountOverride: effectiveDiscountOverride ?? 0,
    maximumDiscount,
    displayTotal,
    items,
    deliveryCost,
    promotionEndDate: getPromotionEndDate(pricedProductsWithoutDelivery),
  };
};
